import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";

const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

const useFetchStrapiData = (collection) => {
  const [data, setData] = useState([]);
  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;
  const locale = getLocale();

  useEffect(() => {
    const fetchData = async () => {
      if (!STRAPI_URL || !STRAPI_TOKEN) {
        console.warn("Missing STRAPI_URL or STRAPI_TOKEN.");
        setData([]);
        return;
      }

      const path = `/api/${collection}?sort=ShotDate:desc&populate=Cover&locale=${locale}`;
      const url = new URL(STRAPI_URL + path);

      try {
        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${STRAPI_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Fetch failed: ${response.status} - ${text}`);
        }

        const result = await response.json();
        setData(result.data || []);
      } catch (err) {
        console.error("❌ Fetch error:", err.message);
        setData([]);
      }
    };

    fetchData();
  }, [collection, STRAPI_URL, STRAPI_TOKEN, locale]);

  return { data };
};

const AllPhotos = () => {
  const { data } = useFetchStrapiData("portfolios");
  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
  const { t } = useTranslation();
  const locale = getLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [visibleItems, setVisibleItems] = useState(6);

  const initialYears = searchParams.getAll("year").map(Number);
  const initialCountries = searchParams.getAll("country");

  const [selectedYears, setSelectedYears] = useState(initialYears);
  const [selectedCountries, setSelectedCountries] = useState(initialCountries);

  useEffect(() => {
    const params = new URLSearchParams();
    selectedYears.forEach((y) => params.append("year", y));
    selectedCountries.forEach((c) => params.append("country", c));
    setSearchParams(params);
  }, [selectedYears, selectedCountries, setSearchParams]);

  const getLocalizedCountry = (code) => {
    try {
      return new Intl.DisplayNames([locale], { type: "region" }).of(code);
    } catch {
      return code;
    }
  };

  const availableYears = useMemo(() => {
    const years = data
      .map((item) => new Date(item.ShotDate).getFullYear())
      .filter((y) => !isNaN(y));
    return Array.from(new Set(years)).sort((a, b) => b - a);
  }, [data]);

  const availableCountries = useMemo(() => {
    const countries = data.map((item) => item.Country).filter(Boolean);
    return Array.from(new Set(countries)).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const year = item.ShotDate ? new Date(item.ShotDate).getFullYear() : null;
      const matchYear = selectedYears.length === 0 || (year && selectedYears.includes(year));
      const matchCountry = selectedCountries.length === 0 || selectedCountries.includes(item.Country);
      return matchYear && matchCountry;
    });
  }, [data, selectedYears, selectedCountries]);

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        setVisibleItems((prev) => Math.min(prev + 3, filteredData.length));
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [filteredData]);

  const toggleYear = (year) => {
    setVisibleItems(6);
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  const toggleCountry = (country) => {
    setVisibleItems(6);
    setSelectedCountries((prev) =>
      prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
    );
  };

  return (
    <>
      <div className="container mt-5 mt-xl-10">
        <div className="row g-3 g-md-5 align-items-end mb-5">
          <div className="col-12 text-end">
            <ul className="list-inline">
              <li className="list-inline-item ms-2">
                <a
                  className="underline text-black"
                  data-bs-toggle="offcanvas"
                  href="#offcanvasFilter"
                  role="button"
                  aria-controls="offcanvasFilter"
                >
                  {t("photos.filters", "Filter")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="row g-3 g-lg-5">
          {filteredData.slice(0, visibleItems).map((item) => {
            const localizedCountry = item.Country ? getLocalizedCountry(item.Country) : "";
            const imageUrl = item.Cover?.url
              ? `${STRAPI_URL}${item.Cover.url}`
              : "/default-image.jpg";

            const hasPlace = !!item.place;
            const hasCountry = !!localizedCountry;

            return (
              <div className="col-md-6 col-lg-4" key={item.id}>
                <div className="product">
                  <a
                    href={`/photos/${item.slug}`}
                    className="card equal-16-10"
                    style={{ borderRadius: "0px", overflow: "hidden" }}
                  >
                    <div className="card-wrap p-0" style={{ borderRadius: "0px" }} />
                    <figure
                      className="background"
                      style={{
                        backgroundImage: `url(${imageUrl})`,
                        borderRadius: "0px",
                      }}
                    />
                  </a>

                  <a
                    className="product-title mt-1"
                    href={`/photos/${item.slug}`}
                    style={{
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      display: "block",
                    }}
                  >
                    {item.title}
                  </a>

                  <span
                    className="product-price"
                    style={{ textTransform: "uppercase", display: "block", lineHeight:"100% "}}
                  >
                    {(hasPlace || hasCountry) && (
                      <>
                        {hasPlace && item.place}
                        {hasPlace && hasCountry && ", "}
                        {hasCountry && localizedCountry}
                        <br />
                      </>
                    )}

                    {item.ShotDate && (
                      <>
                        {new Date(item.ShotDate).toLocaleDateString(locale, {
                          month: "long",
                          year: "numeric",
                        })}
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Offcanvas Filter */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="offcanvasFilter"
        aria-labelledby="offcanvasFilterLabel"
        aria-hidden="true"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasFilterLabel">
            {t("photos.filters", "Filters")}
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>

        <div className="offcanvas-body">
          {/* Year Filter */}
          <div className="widget mb-4">
            <span className="d-flex eyebrow text-muted mb-2">{t("photos.year", "Year")}</span>
            <ul className="list-unstyled">
              {availableYears.map((year) => (
                <li key={year} className="mt-1">
                  <div className="form-check form-check-minimal">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`year-${year}`}
                      checked={selectedYears.includes(year)}
                      onChange={() => toggleYear(year)}
                    />
                    <label className="form-check-label" htmlFor={`year-${year}`}>
                      {year}
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Country Filter */}
          <div className="widget">
            <span className="d-flex eyebrow text-muted mb-2">{t("photos.country", "Country")}</span>
            <ul className="list-unstyled">
              {availableCountries.map((code) => (
                <li key={code} className="mt-1">
                  <div className="form-check form-check-minimal">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`country-${code}`}
                      checked={selectedCountries.includes(code)}
                      onChange={() => toggleCountry(code)}
                    />
                    <label className="form-check-label" htmlFor={`country-${code}`}>
                      {getLocalizedCountry(code)}
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllPhotos;
